import {useUpmPlant} from '@hconnect/upm'
import {
  Alert,
  ControlledAutocompleteSelect,
  H4,
  Icon,
  TextMedium,
  TextSemiBold
} from '@hm/fundamental'
import {mdiInformationOutline} from '@mdi/js'
import {useEffect} from 'react'
import {Controller, UseFormReturn} from 'react-hook-form'
import {useTranslation} from 'react-i18next'

import {BrickCalculationSection} from './BrickCalculationSection'

import {useKilns} from '@refra/modules/common/providers'
import {
  useBrickCalculationQuery,
  useBrickShapesQuery
} from '@refra/modules/refra-planning/hooks/queries'
import {
  BrickCalculationForm,
  EditMaterialInstallationForm,
  LiningQuality,
  type MaterialInstallation,
  type MaterialInstallationDetails
} from '@refra/modules/refra-planning/types'
import {useUrlParam} from '@refra/modules/routing/hooks'

export interface BrickCalculationProps {
  shutdownId: number
  materialInstallation: MaterialInstallation | MaterialInstallationDetails
  formMethods: UseFormReturn<EditMaterialInstallationForm>
  isReadOnly?: boolean
}

export const BrickCalculation = ({
  shutdownId,
  materialInstallation,
  formMethods,
  isReadOnly = false
}: BrickCalculationProps) => {
  const {t} = useTranslation()
  const plantId = useUrlParam('plantCode')
  const upmPlant = useUpmPlant({plantId})

  const {selectedKiln} = useKilns()

  const {data: brickShapes, isFetching: isFetchingBrickShapes} = useBrickShapesQuery()
  const values = formMethods.watch('brickCalculations')
  const brickShape1 = values?.brickShape1 ?? ''
  const brickShape2 = values?.brickShape2 ?? ''
  const keyBricksCount = values?.keyBricksCount ?? 2
  const useRefratechnikKeyBricks = values?.useRefratechnikKeyBricks ?? false

  const {data: brickCalculation, isFetching: isFetchingBrickCalculation} = useBrickCalculationQuery(
    {
      plantUpmId: upmPlant.upmId,
      kilnUpmId: selectedKiln.upmId,
      shutdownId,
      installationId: materialInstallation.id,
      brickShape1,
      brickShape2,
      keyBricksCount,
      useRefratechnikKeyBricks,
      enabled: brickShape1 !== '' && brickShape2 !== ''
    }
  )

  const displayData =
    brickCalculation ??
    ('brickCalculation' in materialInstallation ? materialInstallation.brickCalculation : undefined)

  useEffect(() => {
    if (brickCalculation) {
      formMethods.setValue('brickCalculations.sections', brickCalculation.sections, {
        shouldDirty: false
      })
    }
  }, [brickCalculation, formMethods])

  const brickShapesOptions = brickShapes?.map((brickShape) => ({
    label: brickShape.name,
    value: brickShape.name
  }))

  const formDisabled = isFetchingBrickCalculation || isFetchingBrickShapes || isReadOnly

  return (
    <div className="flex flex-col gap-2" data-cy="brick_calculation_tab">
      <Alert variant={'info'} className="mb-3">
        <div className="flex items-center gap-2">
          <Icon size="large" className="text-primary" path={mdiInformationOutline} />
          {t('refraPlanning.brickCalculation.info')}
        </div>
      </Alert>
      <div className="space-y-4 ">
        <div className="flex flex-col gap-2">
          <H4>{t('refraPlanning.brickCalculation.kilnBricks')}</H4>
          <div className="flex flex-row gap-2 p-2">
            <Controller
              control={formMethods.control}
              rules={{required: t('refraPlanning.brickCalculation.brickShapeRequired')}}
              render={({field: {value, onChange}, fieldState: {error}}) => (
                <ControlledAutocompleteSelect
                  label={t('refraPlanning.brickCalculation.brickShape1')}
                  options={brickShapesOptions ?? []}
                  selectedValues={[value ?? '']}
                  isDisabled={formDisabled}
                  error={error?.message}
                  isTouched={!!error?.message}
                  onChange={(selected) => onChange(String(selected[0] ?? ''))}
                />
              )}
              name={'brickCalculations.brickShape1'}
            />
            <Controller
              control={formMethods.control}
              rules={{required: t('refraPlanning.brickCalculation.brickShapeRequired')}}
              render={({field: {value, onChange}, fieldState: {error}}) => (
                <ControlledAutocompleteSelect
                  label={t('refraPlanning.brickCalculation.brickShape2')}
                  options={brickShapesOptions ?? []}
                  selectedValues={[value ?? '']}
                  isDisabled={formDisabled}
                  error={error?.message}
                  isTouched={!!error?.message}
                  onChange={(selected) => onChange(String(selected[0] ?? ''))}
                />
              )}
              name={'brickCalculations.brickShape2'}
            />
          </div>
        </div>
        <div className="flex flex-col gap-2">
          <H4>{t('refraPlanning.brickCalculation.keyBricks')}</H4>
          <div className="flex flex-row gap-2 p-2">
            <Controller
              control={formMethods.control}
              render={({field: {value, onChange}, fieldState: {error}}) => (
                <ControlledAutocompleteSelect
                  label={t('refraPlanning.brickCalculation.supplier')}
                  options={[
                    {
                      label: t('refraPlanning.brickCalculation.supplierOptions.refraTechnik'),
                      value: 'true'
                    },
                    {
                      label: t('refraPlanning.brickCalculation.supplierOptions.other'),
                      value: 'false'
                    }
                  ]}
                  selectedValues={[String(value ?? false)]}
                  isDisabled={formDisabled}
                  error={error?.message}
                  isTouched={!!error?.message}
                  onChange={(selected) => onChange(selected[0] === 'true')}
                />
              )}
              name={'brickCalculations.useRefratechnikKeyBricks'}
            />
            <Controller
              control={formMethods.control}
              render={({field: {value, onChange}, fieldState: {error}}) => (
                <ControlledAutocompleteSelect
                  label={t('refraPlanning.brickCalculation.keyBricksCount')}
                  options={[
                    {
                      label: '2',
                      value: 2
                    },
                    {
                      label: '3',
                      value: 3
                    }
                  ]}
                  selectedValues={[value ?? 2]}
                  isDisabled={formDisabled}
                  error={error?.message}
                  isTouched={!!error?.message}
                  onChange={(selected) => onChange(Number(selected[0]))}
                />
              )}
              name={'brickCalculations.keyBricksCount'}
            />
          </div>
        </div>
      </div>
      <div className="flex flex-col gap-2">
        <H4>{t('refraPlanning.brickCalculation.results')}</H4>
        <div className="flex flex-col gap-4 p-2">
          {displayData?.liningQuantity === LiningQuality.Impossible && (
            <Alert variant={'error'} className="mb-3">
              {t('refraPlanning.brickCalculation.liningImpossible')}
            </Alert>
          )}
          {displayData?.liningQuantity === LiningQuality.Suboptimal && (
            <Alert variant={'error'} className="mb-3">
              {t('refraPlanning.brickCalculation.liningSuboptimal')}
            </Alert>
          )}
          {displayData?.containsCone && (
            <Alert variant={'error'} className="mb-3">
              {t('refraPlanning.brickCalculation.coneError')}
            </Alert>
          )}
          {displayData?.sections.map((section, sectionIndex) => (
            <BrickCalculationSection
              key={section.fromMeter}
              section={section}
              brickShape1={brickShape1}
              brickShape2={brickShape2}
              sectionIndex={sectionIndex}
              formMethods={formMethods}
              isReadOnly={isReadOnly}
            />
          ))}
        </div>
        <div className="flex flex-row gap-2 justify-start p-2">
          <div className="flex flex-col gap-2">
            <TextSemiBold>{t('refraPlanning.brickCalculation.totalBricks')}</TextSemiBold>
            <TextMedium>{displayData?.numberOfBricks}</TextMedium>
          </div>
          <div className="flex flex-col gap-2">
            <TextSemiBold>{t('refraPlanning.brickCalculation.totalWeight')}</TextSemiBold>
            <TextMedium>{displayData?.weightKg.toFixed(2)}</TextMedium>
          </div>
        </div>
      </div>
    </div>
  )
}
